import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { CommandController } from '../../../core/component/base/command-controller.base';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { Command, CommandBase } from '../../../core/model/command';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandHandlerResolver } from '../../../core/service/command/command-handler-resolver.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { LeaseAgreementCategory } from '../../model/const/enums';
import { LeaseAgreementForm } from '../../model/lease-agreement-form';
import { FastighetsagarnaDokumentService } from '../../service/data/fastighetsagarna-dokument.service';

@Component({
    selector: 'app-form-cases-fastighetsagarna-dokument',
    templateUrl: './form-cases-fastighetsagarna-dokument.component.html',
    styleUrls: ['./form-cases-fastighetsagarna-dokument.component.scss'],
    providers: [CommandDispatcher, FastighetsagarnaDokumentService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FormCasesFastighetsagarnaDokumentComponent extends CommandController implements OnInit {
  constructor(
    handlerResolver: CommandHandlerResolver,
    columnDefinitionFactory: ColumnDefinitionFactory,
    private _nativeWindow: NativeWindowService,
    private _service: FastighetsagarnaDokumentService,
    private fb: FormBuilder,
    private fcf: FormControlExFactory,
    commandDispatcher: CommandDispatcher
  ) {
    super(commandDispatcher, handlerResolver);
    this.columnDefinition = [
      columnDefinitionFactory.create('FASTIGHETSAGARNA_DOKUMENT.FORM_CAPTION', 'codeCaption'),
      columnDefinitionFactory.createDateTime('COMMON.CREATED_DATE', 'created'),
    ];
    this.menuDefinition = [
      new MenuItemDefinition('EDIT_BTN', 'editFormCaseRow'),
      new MenuItemDefinition('DELETE_BTN', 'deleteFormCaseRow'),
    ];

    this.fdFormGroup = fb.group({
      formTemplate: fcf.create('FASTIGHETSAGARNA_DOKUMENT.TEMPLATE_SELECTION', 'Ingen'),
      getTemplates: fcf.create('FASTIGHETSAGARNA_DOKUMENT.NEW_FORM_CASE', null),
    });
  }

  @Input() form: AbstractControl;
  @Input() itemsSource: LeaseAgreementForm[];
  @Input() leaseAgreementId: string;
  @Input() leaseAgreementCategory: LeaseAgreementCategory;

  private _isNew: boolean;
  columnDefinition: ColumnDefinition[];
  menuDefinition: MenuItemDefinition[] = [];
  selectedRow: LeaseAgreementForm;

  readonly fdFormGroup: FormGroup;
  fdTemplates = [];
  fdTemplatesDescription = [];

  get fdFormIsSeleceted(): boolean {
    if (this.fdFormGroup.controls['formTemplate'].value) {
      return this.fdFormGroup.controls['formTemplate'].value !== 'Ingen' && this.createNewFormCaseVisible;
    }
    return false;
  }

  get createNewFormCaseVisible(): boolean {
    if (this.fdFormGroup.controls['getTemplates'].value) {
      return this.fdFormGroup.controls['getTemplates'].value === true;
    }
    return false;
  }

  get formDescription(): string {
    if (this.fdFormGroup.controls['formTemplate'].value) {
      let form = this.fdTemplatesDescription.find(x => x.value === this.fdFormGroup.controls['formTemplate'].value);
      return form.description;
    }
    return '';
  }

  get formCodeCaption(): string {
    if (this.fdFormGroup.controls['formTemplate'].value) {
      let form = this.fdTemplates.find(x => x.value === this.fdFormGroup.controls['formTemplate'].value);
      return form.name;
    }
    return '';
  }

  private _deleteItem(row: LeaseAgreementForm) {
    let index = this.itemsSource.indexOf(row);
    this.selectedRow = this.itemsSource.find(x => x === row);
    if (index > -1) {
      let serviceResult = this._service.deleteFormCase(this.leaseAgreementId, this.selectedRow.code);
      this.AddSubscription(
        serviceResult.subscribe(res => {
          this.itemsSource.splice(index, 1);
        })
      );
    }
  }

  createFormCase() {
    let serviceResult = this._service.createFormCase(this.leaseAgreementId, this.fdFormGroup.controls['formTemplate'].value);
    const tab = this._nativeWindow.newTab();
    this.AddSubscription(
      serviceResult.subscribe(res => {
        let row = new LeaseAgreementForm();
        row.codeCaption = this.formCodeCaption;
        row.code = this.fdFormGroup.controls['formTemplate'].value;
        row.created = new Date();
        this.itemsSource.push(row);
        this._nativeWindow.navigate(tab, res);
      })
    );
  }

  private _editItem(row: LeaseAgreementForm) {
    this.selectedRow = this.itemsSource.find(x => x === row);
    let serviceResult = this._service.openFormCase(this.leaseAgreementId, this.selectedRow.code);
    this.AddSubscription(serviceResult.subscribe(res => this._nativeWindow.openTab(res)));
  }

  canExecute(command: CommandBase): boolean {
    switch (command.id) {
      case 'editFormCaseRow':
      case 'deleteFormCaseRow':
        return true;

      default:
        return false;
    }
  }

  execute(command: CommandBase): void {
    switch (command.id) {
      case 'deleteFormCaseRow':
        this._deleteItem((<Command>command).context);
        break;

      case 'editFormCaseRow':
        this._editItem((<Command>command).context);
        break;
    }
  }

  onRowClicked(e: { context: LeaseAgreementForm; event: any }) {
    this._editItem(e.context);
  }

  ngOnInit() {
    switch (this.leaseAgreementCategory) {
      case LeaseAgreementCategory.Dwelling: {
        this.fdFormGroup.controls['getTemplates'].valueChanges.subscribe(value => {
          if (value) {
            this._service.getDwellingTemplates().subscribe(data => {
              this.fdTemplates = [{ name: 'Ingen vald', value: 'Ingen' }];
              data.forEach(item => {
                this.fdTemplates.push({ name: item.codeName, value: item.code });
              });
              this.fdTemplatesDescription = [{ description: 'Ingen vald', value: 'Ingen' }];
              data.forEach(item => {
                this.fdTemplatesDescription.push({ description: item.description, value: item.code });
              });
              this.fdFormGroup.controls['formTemplate'].setValue('10A');
            });
          }
        });
        break;
      }

      case LeaseAgreementCategory.Other: {
        this.fdFormGroup.controls['getTemplates'].valueChanges.subscribe(value => {
          if (value) {
            this._service.getOtherTemplates().subscribe(data => {
              this.fdTemplates = [{ name: 'Ingen vald', value: 'Ingen' }];
              data.forEach(item => {
                this.fdTemplates.push({ name: item.codeName, value: item.code });
              });
              this.fdTemplatesDescription = [{ description: 'Ingen vald', value: 'Ingen' }];
              data.forEach(item => {
                this.fdTemplatesDescription.push({ description: item.description, value: item.code });
              });
            });
          }
        });
        break;
      }

      case LeaseAgreementCategory.Parking: {
        this.fdFormGroup.controls['getTemplates'].valueChanges.subscribe(value => {
          if (value) {
            this._service.getParkingTemplates().subscribe(data => {
              this.fdTemplates = [{ name: 'Ingen vald', value: 'Ingen' }];
              data.forEach(item => {
                this.fdTemplates.push({ name: item.codeName, value: item.code });
              });
              this.fdTemplatesDescription = [{ description: 'Ingen vald', value: 'Ingen' }];
              data.forEach(item => {
                this.fdTemplatesDescription.push({ description: item.description, value: item.code });
              });
              this.fdFormGroup.controls['formTemplate'].setValue('3');
            });
          }
        });
        break;
      }

      case LeaseAgreementCategory.Premises: {
        this.fdFormGroup.controls['getTemplates'].valueChanges.subscribe(value => {
          if (value) {
            this._service.getPremisesTemplates().subscribe(data => {
              this.fdTemplates = [{ name: 'Ingen vald', value: 'Ingen' }];
              data.forEach(item => {
                this.fdTemplates.push({ name: item.codeName, value: item.code });
              });
              this.fdTemplatesDescription = [{ description: 'Ingen vald', value: 'Ingen' }];
              data.forEach(item => {
                this.fdTemplatesDescription.push({ description: item.description, value: item.code });
              });
            });
          }
        });
        break;
      }

      default: {
        this.fdTemplates = [{ name: 'Ingen vald', value: 'Ingen' }];
        break;
      }
    }
  }
}
